/**
 * Pré-remplissage d'une fiche programme à partir d'un fichier média.
 *
 * L'opérateur relit et corrige tout avant de confirmer : ces règles cherchent
 * une proposition plausible, pas une vérité. Elles restent volontairement
 * littérales — mieux vaut un titre à retoucher qu'un titre inventé.
 */

const DATE_PREFIX = /^\d{8}[_-]/
const ID_PREFIX = /^E\d+[_-]/i
/** Suffixe de partie `_Pn` en fin de tige : technique, jamais éditorial. */
const PART_SUFFIX = /[_-]P\d{1,2}$/i

export interface MediaProgramDraft {
  /** Titre proposé, séparateurs techniques rendus lisibles. */
  title: string
  /**
   * Code programme proposé, seulement quand le nom suit la convention maison
   * (`AAAAMMJJ_CODE` ou `E{n}_CODE`) : hors de ce cadre, deviner un code
   * reviendrait à en inventer un.
   */
  programCode: string
}

/** Tige du nom de fichier : sans dossier ni extension. */
export function fileNameStem(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? fileName
  const dot = base.lastIndexOf('.')
  return dot > 0 ? base.slice(0, dot) : base
}

export function draftProgramFromFileName(fileName: string): MediaProgramDraft {
  const stem = fileNameStem(fileName).trim()
  const prefixed = DATE_PREFIX.test(stem) || ID_PREFIX.test(stem)
  const body = stem.replace(DATE_PREFIX, '').replace(ID_PREFIX, '')
  const codeBody = body.replace(PART_SUFFIX, '')

  return {
    title: humanize(body) || stem,
    programCode: prefixed ? codeBody.toUpperCase() : '',
  }
}

/** `4/3` en dessous de 1.5 : au-delà on est déjà en 16/9 ou plus large. */
export function isFourThreeResolution(resolution: string | null | undefined): boolean {
  const match = /^(\d+)\s*[x×]\s*(\d+)$/i.exec(resolution?.trim() ?? '')
  if (!match) return false
  const height = Number(match[2])
  if (!height) return false
  return Number(match[1]) / height < 1.5
}

function humanize(text: string): string {
  return text.replace(/[_.]+/g, ' ').replace(/\s+/g, ' ').trim()
}
